import React, { useState, useEffect, useRef, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  Calendar as CalendarIcon,
  Clock,
  Briefcase,
  AlertTriangle,
  CheckCircle,
  Plus,
  Filter,
  X,
  ExternalLink,
  User,
  Trash2,
  Bell
} from 'lucide-react';
import api from '../api';
import ConfirmModal from './ConfirmModal';
import { useFeatures } from './FeatureGate';
import './StudentRealTimeCalendar.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const CATEGORY_COLORS = {
  PLACEMENT_DRIVES: '#ec4899',
  COURSES: '#06b6d4',
  PERSONAL_REMINDERS: '#ef4444',
  INTERVIEWS: '#8b5cf6'
};

const CATEGORY_LABELS = {
  ALL: 'All Events',
  PLACEMENT_DRIVES: 'Placement Drives',
  COURSES: 'Courses',
  PERSONAL_REMINDERS: 'Personal Reminders',
  INTERVIEWS: 'Interviews'
};

export default function StudentRealTimeCalendar({ userId, profile }) {
  const { isSubEnabled } = useFeatures();
  // Server enforces the same policy (403); this only hides the edit affordances.
  const canManageEvents = isSubEnabled('schedule', 'schedule_personal_events');
  const [scheduleData, setScheduleData] = useState({
    events: [],
    summary_cards: {
      today_events_count: 0,
      upcoming_deadlines_count: 0,
      placement_activities_count: 0,
      pending_assessments_count: 0
    },
    upcoming_widgets: { today: [], tomorrow: [], this_week: [] },
    deadlines_widget: [],
    alerts: []
  });

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'danger',
    onConfirm: null
  });
  const closeConfirmModal = () => setConfirmModal(prev => ({ ...prev, isOpen: false }));

  const [activeFilter, setActiveFilter] = useState('ALL');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [toastAlert, setToastAlert] = useState(null);
  const [isWsConnected, setIsWsConnected] = useState(false);
  const wsRef = useRef(null);

  // Form State for Adding Personal Event
  const [newEventForm, setNewEventForm] = useState({
    title: '',
    description: '',
    event_type: 'PERSONAL_REMINDERS',
    start_datetime: '',
    end_datetime: '',
    reminder_enabled: true,
    reminder_time: 15,
    meeting_link: ''
  });

  const fetchSchedule = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await api.get(`/schedule/student/${userId}`);
      setScheduleData(res.data);

      // If any real-time alert fires within the hour, show a subtle toast
      if (res.data.alerts && res.data.alerts.length > 0) {
        setToastAlert(res.data.alerts[0]);
        setTimeout(() => setToastAlert(null), 8000);
      }
    } catch (err) {
      console.error("Failed to fetch schedule data:", err);
    }
  }, [userId]);

  // Polling fallback setup for Schedule Sync
  useEffect(() => {
    fetchSchedule();

    if (!userId) return;

    // Poll every 60 seconds as a fallback since WebSockets are disabled
    const intervalId = setInterval(() => {
      fetchSchedule();
    }, 60000);

    return () => {
      clearInterval(intervalId);
    };
  }, [userId, fetchSchedule]);

  // Filter events for FullCalendar display
  const allowedEventTypes = ['PLACEMENT_DRIVES', 'COURSES', 'PERSONAL_REMINDERS', 'JOB_APPLICATIONS', 'LIVE_SESSIONS'];

  const filteredEvents = (activeFilter === 'ALL'
    ? scheduleData.events.filter(e => allowedEventTypes.includes(e.event_type))
    : scheduleData.events.filter(e => e.event_type === activeFilter))
    .map(evt => ({
      id: String(evt.id),
      title: evt.title,
      start: evt.start_datetime,
      end: evt.end_datetime || evt.start_datetime,
      backgroundColor: CATEGORY_COLORS[evt.event_type] || '#3b82f6',
      borderColor: CATEGORY_COLORS[evt.event_type] || '#3b82f6',
      extendedProps: { ...evt }
    }));

  const handleEventClick = (info) => {
    const props = info.event.extendedProps;
    setSelectedEvent(props);
  };

  const handleCreatePersonalEvent = async (e) => {
    e.preventDefault();
    if (!newEventForm.title || !newEventForm.start_datetime) return;

    try {
      await api.post(`/schedule/student/${userId}/personal`, newEventForm);
      setShowAddModal(false);
      setNewEventForm({
        title: '',
        description: '',
        event_type: 'PERSONAL_REMINDERS',
        start_datetime: '',
        end_datetime: '',
        reminder_enabled: true,
        reminder_time: 15,
        meeting_link: ''
      });
      fetchSchedule();
    } catch (err) {
      console.error("Error creating personal event:", err);
    }
  };

  const handleDeletePersonalEvent = async (dbId) => {
    if (!dbId) return;
    setConfirmModal({
      isOpen: true,
      title: 'Delete Event',
      message: 'Are you sure you want to delete this event?',
      type: 'danger',
      onConfirm: async () => {
        closeConfirmModal();
        try {
          await api.delete(`/schedule/student/${userId}/event/${dbId}`);
          setSelectedEvent(null);
          fetchSchedule();
        } catch (err) {
          console.error("Error deleting event:", err);
        }
      }
    });
  };

  const formatDisplayTime = (isoString) => {
    if (!isoString) return '';
    try {
      const dt = new Date(isoString);
      return dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return isoString;
    }
  };

  const formatDisplayDate = (isoString) => {
    if (!isoString) return '';
    try {
      const dt = new Date(isoString);
      return dt.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
      return isoString;
    }
  };

  return (
    <div className="stu-calendar-container">
      {/* Header & Summary Cards */}
      <div className="stu-cal-header-bar">
        <div className="stu-cal-title-section">
          <h2>
            <CalendarIcon size={26} color="#3b82f6" />
            My Real-Time Personal Calendar
            <span className="stu-cal-live-badge">
              <span className="stu-cal-live-pulse" />
              {isWsConnected ? 'Live Sync' : 'Active'}
            </span>
          </h2>
          <p>Automatically synchronized across University, Faculty, TPO, and Personal modules.</p>
        </div>
        {canManageEvents && (
          <button className="stu-cal-add-btn" onClick={() => setShowAddModal(true)}>
            <Plus size={16} /> Add Personal Reminder
          </button>
        )}
      </div>

      {/* Summary Cards Grid */}
      <div className="stu-cal-summary-grid">
        <div className="stu-cal-summary-card">
          <div className="stu-cal-card-icon today">
            <Clock size={24} />
          </div>
          <div className="stu-cal-card-content">
            <h4>Today's Events</h4>
            <p className="stu-cal-card-value">{scheduleData.summary_cards.today_events_count}</p>
          </div>
        </div>

        <div className="stu-cal-summary-card">
          <div className="stu-cal-card-icon placement">
            <Briefcase size={24} />
          </div>
          <div className="stu-cal-card-content">
            <h4>Placement Activities</h4>
            <p className="stu-cal-card-value">{scheduleData.summary_cards.placement_activities_count}</p>
          </div>
        </div>
      </div>

      {/* Category Filters Bar */}
      <div className="stu-cal-filters-section">
        <span className="stu-cal-filter-label">
          <Filter size={14} /> Filter Categories:
        </span>
        {Object.keys(CATEGORY_LABELS).map((key) => {
          const color = CATEGORY_COLORS[key] || '#3b82f6';
          const isActive = activeFilter === key;
          return (
            <button
              key={key}
              className={`stu-cal-filter-pill ${isActive ? 'active' : ''}`}
              style={{ '--pill-color': color }}
              onClick={() => setActiveFilter(key)}
            >
              {key !== 'ALL' && <span className="stu-cal-filter-dot" />}
              {CATEGORY_LABELS[key]}
            </button>
          );
        })}
      </div>

      {/* Main Layout: FullCalendar & Side Widgets */}
      <div className="stu-cal-main-layout">
        {/* Left: FullCalendar Board */}
        <div className="stu-cal-board-panel">
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay'
            }}
            eventTimeFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: 'short'
            }}
            events={filteredEvents}
            eventClick={handleEventClick}
            height="620px"
            navLinks={true}
            editable={false}
            selectable={true}
          />
        </div>

        {/* Right: Side Widgets */}
        <div className="stu-cal-widgets-sidebar">
          {/* Upcoming Events Widget */}
          <div className="stu-cal-widget-box">
            <div className="stu-cal-widget-title">
              <span>📅 Upcoming Events</span>
            </div>

            <div className="stu-cal-widget-subtitle">Today</div>
            <div className="stu-cal-widget-list">
              {scheduleData.upcoming_widgets.today.length > 0 ? (
                scheduleData.upcoming_widgets.today.map((evt, idx) => (
                  <div key={idx} className="stu-cal-widget-item" onClick={() => setSelectedEvent(evt)}>
                    <div className="stu-cal-item-time">{formatDisplayTime(evt.start_datetime)}</div>
                    <div className="stu-cal-item-info">
                      <div className="stu-cal-item-title">{evt.title}</div>
                      <span className="stu-cal-item-badge" style={{ borderLeftColor: CATEGORY_COLORS[evt.event_type] }}>
                        {evt.category_badge || evt.event_type}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="stu-cal-empty-text">No more events scheduled for today.</p>
              )}
            </div>

            <div className="stu-cal-widget-subtitle">Tomorrow</div>
            <div className="stu-cal-widget-list">
              {scheduleData.upcoming_widgets.tomorrow.length > 0 ? (
                scheduleData.upcoming_widgets.tomorrow.map((evt, idx) => (
                  <div key={idx} className="stu-cal-widget-item" onClick={() => setSelectedEvent(evt)}>
                    <div className="stu-cal-item-time">{formatDisplayTime(evt.start_datetime)}</div>
                    <div className="stu-cal-item-info">
                      <div className="stu-cal-item-title">{evt.title}</div>
                      <span className="stu-cal-item-badge">{evt.category_badge || evt.event_type}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="stu-cal-empty-text">No events scheduled for tomorrow.</p>
              )}
            </div>
          </div>

      </div>
      </div>

      {/* Event Details Modal */}
      {selectedEvent && (
        <div className="stu-cal-modal-overlay" onClick={() => setSelectedEvent(null)}>
          <div className="stu-cal-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="stu-cal-modal-header">
              <h3>Event Details</h3>
              <button className="stu-cal-modal-close" onClick={() => setSelectedEvent(null)}>
                <X size={20} />
              </button>
            </div>

            <div className="stu-cal-modal-body">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span
                  style={{
                    padding: '4px 10px',
                    borderRadius: '16px',
                    fontSize: '11px',
                    fontWeight: '700',
                    background: CATEGORY_COLORS[selectedEvent.event_type] || '#3b82f6',
                    color: '#ffffff'
                  }}
                >
                  {selectedEvent.category_badge || selectedEvent.event_type}
                </span>
                <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>
                  Module: {selectedEvent.source_module}
                </span>
              </div>

              <h4 style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', margin: '4px 0' }}>
                {selectedEvent.title}
              </h4>

              {selectedEvent.description && (
                <div className="stu-cal-detail-row">
                  <Briefcase size={16} />
                  <span>{selectedEvent.description}</span>
                </div>
              )}

              <div className="stu-cal-detail-row">
                <Clock size={16} />
                <span>
                  {formatDisplayDate(selectedEvent.start_datetime)} • {formatDisplayTime(selectedEvent.start_datetime)}
                  {selectedEvent.end_datetime && ` - ${formatDisplayTime(selectedEvent.end_datetime)}`}
                </span>
              </div>

              {selectedEvent.faculty_or_tpo_name && (
                <div className="stu-cal-detail-row">
                  <User size={16} />
                  <span>Host / Organizer: <strong>{selectedEvent.faculty_or_tpo_name}</strong></span>
                </div>
              )}

              {selectedEvent.meeting_link && (
                <div style={{ marginTop: '6px' }}>
                  <a
                    href={selectedEvent.meeting_link}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: '#eff6ff',
                      color: '#2563eb',
                      padding: '10px 16px',
                      borderRadius: '10px',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: '13px'
                    }}
                  >
                    <ExternalLink size={16} /> Join Meeting Link
                  </a>
                </div>
              )}

              {/* Action Buttons */}
              <div className="stu-cal-modal-actions">
                {canManageEvents && selectedEvent.source_module === 'STUDENT' && selectedEvent.db_id && (
                  <button
                    className="stu-cal-btn-delete"
                    onClick={() => handleDeletePersonalEvent(selectedEvent.db_id)}
                  >
                    <Trash2 size={15} style={{ marginRight: '6px', verticalAlign: 'middle' }} /> Delete Reminder
                  </button>
                )}
                <button className="stu-cal-btn-primary" onClick={() => setSelectedEvent(null)}>
                  Close Details
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Personal Reminder Modal */}
      {showAddModal && (
        <div className="stu-cal-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="stu-cal-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="stu-cal-modal-header">
              <h3>Create Personal Reminder</h3>
              <button className="stu-cal-modal-close" onClick={() => setShowAddModal(false)}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleCreatePersonalEvent} className="stu-cal-modal-body">
              <div className="stu-cal-form-group">
                <label>Title *</label>
                <input
                  type="text"
                  placeholder="e.g. Study for AI Midterm"
                  value={newEventForm.title}
                  onChange={(e) => setNewEventForm({ ...newEventForm, title: e.target.value })}
                  required
                />
              </div>

              <div className="stu-cal-form-group">
                <label>Category</label>
                <select
                  value={newEventForm.event_type}
                  onChange={(e) => setNewEventForm({ ...newEventForm, event_type: e.target.value })}
                >
                  <option value="PERSONAL_REMINDERS">🔔 Personal Reminder</option>
                  <option value="CLASSES">📚 Study Class / Block</option>
                  <option value="ASSIGNMENTS">📝 Assignment Target</option>
                  <option value="COURSES">📚 Course Schedule</option>
                </select>
              </div>

              <div className="stu-cal-form-group">
                <label>Start Date & Time *</label>
                <input
                  type="datetime-local"
                  value={newEventForm.start_datetime}
                  onChange={(e) => setNewEventForm({ ...newEventForm, start_datetime: e.target.value })}
                  required
                />
              </div>

              <div className="stu-cal-form-group">
                <label>Description / Notes</label>
                <textarea
                  rows="3"
                  placeholder="Additional details..."
                  value={newEventForm.description}
                  onChange={(e) => setNewEventForm({ ...newEventForm, description: e.target.value })}
                />
              </div>

              <div className="stu-cal-form-group">
                <label>Meeting Link (Optional)</label>
                <input
                  type="url"
                  placeholder="https://..."
                  value={newEventForm.meeting_link}
                  onChange={(e) => setNewEventForm({ ...newEventForm, meeting_link: e.target.value })}
                />
              </div>

              <div className="stu-cal-modal-actions">
                <button type="button" className="stu-cal-btn-delete" style={{ borderColor: '#cbd5e1', color: '#475569', background: '#f8fafc' }} onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="stu-cal-btn-primary">
                  Save Reminder
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast Notification for Real-Time Reminder Alerts */}
      {toastAlert && (
        <div className="stu-cal-toast-alert">
          <Bell size={22} color="#10b981" />
          <div>
            <div style={{ fontWeight: '800', fontSize: '13px' }}>🔔 {toastAlert.title}</div>
            <div style={{ fontSize: '12px', color: '#cbd5e1' }}>{toastAlert.time_left}</div>
          </div>
          <button
            onClick={() => setToastAlert(null)}
            style={{ background: 'transparent', border: 'none', color: '#cbd5e1', cursor: 'pointer', marginLeft: 'auto' }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      <ConfirmModal 
        {...confirmModal} 
        onCancel={closeConfirmModal} 
      />
    </div>
  );
}
